import { t, Trans } from "@lingui/macro";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { DashboardContent } from "../../components/layout/dashboard-content";
import { TemplatePickerDialog } from "../../templates/components/template-picker-dialog";
import type { DocumentTemplate } from "../../templates/data/templates";
import { DocumentGrid } from "../components/document-grid";
import { DocumentsPageHeader } from "../components/documents-page-header";
import { OfferCard } from "../components/offer-card";
import type { DocumentAction, DocumentSort, UserDocument } from "../types/document";
import { CoverLetterCard } from "./cover-letter-card";
import { CoverLetterCreateCard } from "./cover-letter-create-card";
import { useCoverLetterAccess } from "./use-cover-letter-access";

const coverLetters: UserDocument[] = [];

const sortCoverLetters = (documents: UserDocument[], sort: DocumentSort) => {
  return [...documents].sort((first, second) => {
    switch (sort) {
      case "oldest":
        return new Date(first.updatedAt).getTime() - new Date(second.updatedAt).getTime();

      case "title-ascending":
        return first.title.localeCompare(second.title, "fr", { sensitivity: "base" });

      case "title-descending":
        return second.title.localeCompare(first.title, "fr", { sensitivity: "base" });

      case "recent":
      default:
        return new Date(second.updatedAt).getTime() - new Date(first.updatedAt).getTime();
    }
  });
};

export const CoverLettersPage = () => {
  const navigate = useNavigate();
  const { requestAccess, pending, accessDialog } = useCoverLetterAccess();
  const [creationRequested, setCreationRequested] = useState(false);

  const [sort, setSort] = useState<DocumentSort>("recent");

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const sortedCoverLetters = useMemo(() => sortCoverLetters(coverLetters, sort), [sort]);

  const firstCoverLetter = sortedCoverLetters.at(0);

  const remainingCoverLetters = sortedCoverLetters.slice(1);

  const openCreationPage = () => {
    void requestAccess(() => setCreationRequested(true));
  };

  const handleUseTemplate = (_template: DocumentTemplate) => {
    openCreationPage();
  };

  const handleOpen = (coverLetter: UserDocument) => {
    void navigate(`/client/user-space/cover-letters/${coverLetter.id}`);
  };

  const handleAction = (coverLetter: UserDocument, action: DocumentAction) => {
    switch (action) {
      case "open":
      case "download":
        handleOpen(coverLetter);
        break;

      case "rename":
      case "duplicate":
      case "delete":
      default:
        break;
    }
  };

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-4 sm:space-y-5">
        <DocumentsPageHeader
          title="Mes lettres de motivation"
          description="Créez et organisez vos lettres pour chacune de vos candidatures."
          sort={sort}
          onSortChange={setSort}
        />

        <p
          role="status"
          className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-950"
        >
          {creationRequested ? (
            t`Votre pack est actif. L’éditeur de lettres de motivation est encore en préparation.`
          ) : pending ? (
            t`Vérification de votre pack…`
          ) : (
            <Trans>
              La création de lettres nécessite un pack actif. L’éditeur est en préparation.
            </Trans>
          )}
        </p>

        <DocumentGrid>
          <CoverLetterCreateCard
            className="[&>div]:!min-h-[248px] sm:[&>div]:!min-h-[280px] [&_button]:!min-h-[124px] sm:[&_button]:!min-h-[140px]"
            onCreate={() => {
              openCreationPage();
            }}
            onUseTemplate={() => {
              void requestAccess(() => setTemplatePickerOpen(true));
            }}
          />

          {firstCoverLetter && (
            <CoverLetterCard
              coverLetter={firstCoverLetter}
              onOpen={handleOpen}
              onAction={handleAction}
            />
          )}

          <OfferCard className="!min-h-[248px] sm:!min-h-[280px]" />

          {remainingCoverLetters.map((coverLetter) => (
            <CoverLetterCard
              key={coverLetter.id}
              coverLetter={coverLetter}
              onOpen={handleOpen}
              onAction={handleAction}
            />
          ))}
        </DocumentGrid>
      </section>

      {accessDialog}
      <TemplatePickerDialog
        open={templatePickerOpen}
        kind="cover-letter"
        onOpenChange={setTemplatePickerOpen}
        onUseTemplate={handleUseTemplate}
      />
    </DashboardContent>
  );
};
